import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { fetchConsultations, acceptConsultation, rejectConsultation, markNoShow, startConsultation } from "@lawyer/services/lawyerApi";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { EmptyState, LiveNowSection, RecordsTable, UpcomingSection } from "@lawyer/components/consultations/ConsultationSections";
import { DateFilter } from "@lawyer/components/earnings/EarningsFilters";

const PRIMARY_TABS = [["live", "Live"], ["upcoming", "Upcoming"], ["completed", "Completed"]];
const MORE_TABS = [["cancelled", "Cancelled"], ["no_show", "Client no-show"], ["lawyer_no_show", "Missed by me"]];
const EMPTY = {
  completed: ["No completed consultations", "No completed consultations yet."],
  cancelled: ["No cancelled consultations", "Nothing has been cancelled or rejected."],
  no_show: ["No client no-shows", "Every client has turned up so far."],
  lawyer_no_show: ["No missed consultations", "You haven't missed any consultation."],
};
const NONE = { total: 0, items: [] };

/**
 * Work-queue view over the existing `/lawyer/consultations?bucket=` endpoint.
 * Live view = live + upcoming buckets (the live bucket is only requested when the shell's
 * dashboard counts — already loaded, no extra call — say something is live). Everything else
 * is one request per selected filter, exactly as before; grouping is done in the browser.
 */
export default function LawyerConsultations({ counts }) {
  const [params, setParams] = useSearchParams();
  const [liveHint, setLiveHint] = useState(false);          // set after "Start" so the live bucket is fetched even with stale counts
  const liveKnown = (counts?.live || 0) > 0 || liveHint;
  const bucket = params.get("bucket") || (counts ? (liveKnown ? "live" : "upcoming") : null);
  const [data, setData] = useState({ upcoming: NONE, live: NONE, other: NONE });
  const [loading, setLoading] = useState(true);
  // Time range (IST days) — applied server-side via from/to on the same endpoint; Live is never filtered.
  const [range, setRange] = useState({ preset: "all", from: null, to: null });
  const { from, to } = range;

  const load = useCallback(async () => {
    if (!bucket) return;
    setLoading(true);
    try {
      if (bucket === "live") {
        const [up, live] = await Promise.all([
          fetchConsultations({ bucket: "upcoming", limit: 50 }),
          liveKnown ? fetchConsultations({ bucket: "live", limit: 50 }) : Promise.resolve(NONE),
        ]);
        setData({ upcoming: up, live, other: NONE });
      } else {
        const r = await fetchConsultations({ bucket, limit: 50, from: from || undefined, to: to || undefined });
        setData(bucket === "upcoming" ? { upcoming: r, live: NONE, other: NONE } : { upcoming: NONE, live: NONE, other: r });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
    setLoading(false);
  }, [bucket, liveKnown, from, to]);

  useEffect(() => { load(); }, [load]);

  // Ticks every 30 s so slot-window locks (Start / Reject / No-show) lift without a refresh — no network.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const handlers = useMemo(() => ({
    now,
    onAccept: async (cid) => { try { await acceptConsultation(cid); toast.success("Accepted"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } },
    onStart: async (cid) => { try { await startConsultation(cid); toast.success("Consultation started"); setLiveHint(true); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } },
    onReject: async (cid) => {
      const reason = window.prompt("Reason for rejection? (shown to client)");
      if (reason == null) return;
      try { await rejectConsultation(cid, reason); toast.success("Rejected"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    },
    onNoShow: async (cid) => {
      if (!window.confirm("Mark as no-show? The client won't be auto-refunded — admin handles that.")) return;
      try { await markNoShow(cid); toast.success("Marked no-show"); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    },
  }), [load, now]);

  const count = (k) => (k === "live" && data.live.total ? data.live.total : counts?.[k]);
  const inMore = MORE_TABS.find(([k]) => k === bucket);
  const tabCls = (active) => `h-11 px-5 rounded-xl text-sm font-extrabold inline-flex items-center gap-2 border transition-colors ${active ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white border-slate-200 text-[#0A2540] hover:border-[#0A2540]"}`;
  const Badge = ({ n, active, red }) => n == null ? null : (
    <span className={`min-w-[22px] h-[22px] px-1.5 rounded-full grid place-items-center text-[11px] font-extrabold ${red ? "bg-rose-600 text-white" : active ? "bg-white/15 text-white" : "bg-slate-100 text-slate-600"}`}>{n}</span>
  );

  return (
    <>
      <div className="mb-6" data-testid="lawyer-consultations-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Consultations</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your live consultations, upcoming bookings and consultation records.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" data-testid="lawyer-consultations-tabs">
        {PRIMARY_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setParams({ bucket: k })} data-testid={`tab-${k}`} className={tabCls(bucket === k)} aria-pressed={bucket === k}>
            {k === "live" && <span className={`h-2.5 w-2.5 rounded-full ${count("live") ? "bg-rose-500" : "bg-slate-300"}`} />}
            {label} <Badge n={count(k)} active={bucket === k} red={k === "live" && !!count("live")} />
          </button>
        ))}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button data-testid="tab-more" className={tabCls(!!inMore)} aria-pressed={!!inMore}>{inMore ? inMore[1] : "More"} <ChevronDown size={15} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 rounded-xl">
            {MORE_TABS.map(([k, label]) => (
              <DropdownMenuItem key={k} onSelect={() => setParams({ bucket: k })} data-testid={`tab-${k}`} className="text-xs font-semibold justify-between">
                {label}<span className="text-slate-400">{counts?.[k] ?? ""}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {bucket && bucket !== "live" && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3" data-testid="consultations-filters">
          <DateFilter key={range.preset === "custom" ? "custom" : "preset"} preset={range.preset} from={range.from} to={range.to}
                      onApply={({ pending, ...next }) => { if (pending) setRange((r) => ({ ...r, preset: "custom" })); else setRange(next); }} />
          {(range.from || range.to) && !loading && (
            <span className="text-xs text-slate-500" data-testid="consultations-filtered-count">{(bucket === "upcoming" ? data.upcoming : data.other).total} in range</span>
          )}
        </div>
      )}

      {loading || !bucket ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400" data-testid="consultations-loading">Loading…</div>
      ) : bucket === "live" ? (
        <div className="space-y-5" data-testid="live-view">
          {data.live.items.length
            ? <LiveNowSection items={data.live.items} handlers={handlers} />
            : <EmptyState title="No live consultations" body="You have no live consultations right now." testId="empty-live" />}
          <UpcomingSection items={data.upcoming.items} handlers={handlers} />
        </div>
      ) : bucket === "upcoming" ? (
        <UpcomingSection items={data.upcoming.items} handlers={handlers} />
      ) : data.other.items.length === 0 ? (
        <EmptyState title={range.from || range.to ? "Nothing in this date range" : EMPTY[bucket][0]} body={range.from || range.to ? "Try a wider range or clear the filter." : EMPTY[bucket][1]} testId={`empty-${bucket}`} />
      ) : (
        <RecordsTable items={data.other.items} handlers={handlers} testId={`records-${bucket}`} />
      )}
    </>
  );
}
